import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@sea-snag/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@sea-snag/ui';
import { Flame, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { Line, LineChart, ReferenceDot, XAxis, YAxis } from 'recharts';

import type { CalendarWeek } from '@/lib/api-client';
import { deriveMarketStatus, type MarketVerdict, type TrendPoint } from '@/lib/market-status';

// Chart palette — one source of truth for the lines, the selected-week dots,
// and the legend. Use the raw --chart-* vars (not the --color-* aliases): the
// legend renders outside ChartContainer where the aliases aren't in scope.
const MARKET_COLOR = 'var(--chart-1)';
const OWN_COLOR = 'var(--chart-2)';

const VERDICT_META: Record<
  Exclude<MarketVerdict, 'none'>,
  { label: string; className: string; Icon: typeof Flame }
> = {
  ahead: {
    label: 'Ahead of market',
    className: 'text-emerald-600 dark:text-emerald-400',
    Icon: TrendingUp,
  },
  inline: {
    label: 'In line with market',
    className: 'text-slate-600 dark:text-slate-300',
    Icon: Minus,
  },
  behind: {
    label: 'Behind the market',
    className: 'text-amber-600 dark:text-amber-400',
    Icon: TrendingDown,
  },
};

const chartConfig = {
  market: { label: 'Market', color: MARKET_COLOR },
  own: { label: 'You', color: OWN_COLOR },
} satisfies ChartConfig;

function formatWeekLabel(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface ChartRow {
  week: string;
  label: string;
  market: number;
  own: number | null;
  isSelected: boolean;
}

function buildChartData(trend: TrendPoint[]): ChartRow[] {
  return trend.map((p) => ({
    week: p.weekStart,
    label: formatWeekLabel(p.weekStart),
    market: p.marketRate,
    own: p.ownRate,
    isSelected: p.isSelected,
  }));
}

export function MarketStatusCard({
  calendar,
  selectedWeekIndex,
  weekLabel,
}: {
  calendar: CalendarWeek[];
  selectedWeekIndex: number;
  weekLabel: string;
}) {
  const status = deriveMarketStatus(calendar, selectedWeekIndex);
  const chartData = buildChartData(status.trend);
  const selectedLabel = chartData.find((r) => r.isSelected)?.label;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Flame className="text-muted-foreground h-4 w-4" />
          Market Status — {weekLabel}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {status.marketRate == null ? (
          <p className="text-muted-foreground text-sm">No competitor boats in this set yet.</p>
        ) : (
          <div className="flex items-center gap-10">
            <div className="shrink-0 space-y-1">
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-3xl font-semibold tabular-nums">
                  {Math.round(status.marketRate * 100)}%
                </span>
                <span className="text-muted-foreground text-sm">market booked</span>
              </div>
              {status.verdict !== 'none' && (
                <VerdictChip verdict={status.verdict} delta={status.delta} />
              )}
              <p className="text-muted-foreground text-xs">
                {status.bookedCount} of {status.totalCompetitors} competitors booked
              </p>
              {status.ownTotal > 0 && (
                <p className="text-muted-foreground text-xs">
                  Your fleet: {status.ownBookedCount} of {status.ownTotal} booked
                </p>
              )}
            </div>

            {chartData.length >= 2 && (
              <div className="min-w-0 flex-1 space-y-1">
                <ChartContainer config={chartConfig} className="h-[112px] w-full">
                  <LineChart data={chartData} margin={{ top: 8, right: 16, left: 16, bottom: 0 }}>
                    {/* Y axis hidden but fixed to 0–100% so both lines share a fair scale. */}
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={6}
                      fontSize={10}
                      interval={0}
                    />
                    <YAxis domain={[0, 1]} hide />
                    <ChartTooltip
                      cursor={false}
                      content={
                        <ChartTooltipContent
                          formatter={(value, name) => (
                            <div className="flex w-full items-center justify-between gap-3">
                              <span className="text-muted-foreground">
                                {name === 'own' ? 'You' : 'Market'}
                              </span>
                              <span className="font-mono font-medium tabular-nums">
                                {value == null ? '—' : `${Math.round(Number(value) * 100)}%`}
                              </span>
                            </div>
                          )}
                        />
                      }
                    />
                    <Line
                      dataKey="market"
                      type="monotone"
                      stroke={MARKET_COLOR}
                      strokeWidth={2}
                      dot={{ r: 1.5 }}
                      isAnimationActive={false}
                    />
                    <Line
                      dataKey="own"
                      type="monotone"
                      stroke={OWN_COLOR}
                      strokeWidth={2}
                      dot={{ r: 1.5 }}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                    {selectedLabel != null && (
                      <ReferenceDot
                        x={selectedLabel}
                        y={status.marketRate}
                        r={3.5}
                        fill={MARKET_COLOR}
                        stroke="var(--background)"
                        strokeWidth={1.5}
                        isFront
                      />
                    )}
                    {selectedLabel != null && status.ownRate != null && (
                      <ReferenceDot
                        x={selectedLabel}
                        y={status.ownRate}
                        r={3.5}
                        fill={OWN_COLOR}
                        stroke="var(--background)"
                        strokeWidth={1.5}
                        isFront
                      />
                    )}
                  </LineChart>
                </ChartContainer>
                <div className="text-muted-foreground flex items-center gap-3 text-[10px]">
                  <LegendDot color={OWN_COLOR} label="You" />
                  <LegendDot color={MARKET_COLOR} label="Market" />
                </div>
              </div>
            )}

            {chartData.length < 2 && (
              <p className="text-muted-foreground flex-1 text-right text-xs">
                Not enough weeks yet to chart the trend.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

function VerdictChip({ verdict, delta }: { verdict: MarketVerdict; delta: number | null }) {
  if (verdict === 'none') return null;
  const { label, className, Icon } = VERDICT_META[verdict];
  const pts = delta == null ? null : Math.round(delta * 100);
  return (
    <span className={`flex items-center gap-1 text-sm font-medium ${className}`}>
      <Icon className="h-4 w-4" />
      {label}
      {pts != null && verdict !== 'inline' && (
        <span className="text-muted-foreground">
          ({pts >= 0 ? '+' : ''}
          {pts}pp)
        </span>
      )}
    </span>
  );
}
